// ─────────────────────────────────────────────────────────────────────────────
// PhoneHost — mounts the Claude Design phone documents inside the AUTHENTICATED
// app, hands them the session token, owns which page is shown, relays the live
// socket, and composites the ONE orb above the reserved centre.
//
// THIS FILE CONTAINS NO DESIGN. It is a frame, a token handoff, a router, a relay
// and an overlay. The visual layer is the three .dc.html documents in
// public/phone/, served verbatim and never compiled.
//
// WHY A FRAME RATHER THAN A REDIRECT:
// server/app.py:2133 — "Authorization header only (no session cookies, no body
// tokens). Token lives in frontend memory only — never localStorage." The token
// exists ONLY as `window.__rexyToken` in this document. A navigation to /phone/
// created a new token-less window, which is why every panel read unavailable.
//
// WHY THE PARENT OWNS NAVIGATION:
// The design's tab bar is real <a href="…dc.html"> anchors. Following them would
// load a new document (losing the token) and 404 into the SPA fallback at the PIN
// screen. The pages cancel the click and ask here; this swaps the frame's src
// against an ALLOWLIST, and the new page asks for the token exactly as before.
//
// WHY THE PARENT RELAYS THE SOCKET:
// Console's vitals / telemetry / proximity / voice lights are Socket.IO
// broadcasts, not REST. A static document cannot run the socket.io client, and a
// second connection would be a second core. This consumes the ONE app-wide
// singleton and forwards each payload in.
//
// WHY THE PARENT OWNS THE ORB:
// RexyOrbThree is React + three.js. Bundling a second WebGL renderer into
// public/ would be exactly the fork this whole arrangement closes. So the page
// MEASURES its reserved slot and reports the rectangle, and the orb is
// composited above it here — imported UNCHANGED, every accepted value intact.
// The slot itself stays empty and keeps its own pointer-events:none; nothing is
// written into it and the orb never becomes part of any template.
//
// WHY THE PARENT OWNS THE VOICE SESSION:
// The compact control's tap starts and ends the call, so it needs the real PTT.
// On this surface the desktop AppShell is never mounted (App.tsx returns here
// before it), so THIS is the single owner of the voice lifecycle — the same role
// AppShell plays on the desktop, never a second session alongside it. Providing
// AppShellLiveContext lets CornerOrb be reused BYTE-FOR-BYTE: all the gesture
// discipline (pointer capture, 5/10/8px thresholds, tap decided on pointer UP and
// only after the gesture has proven it is not a drag, persistence, viewport
// clamping) stays in the one wrapper that already owns it. No gesture code here.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRexySocket } from '../../hooks/useRexySocket';
import { useVoiceDaemonPTT } from '../../hooks/useVoiceDaemonPTT';
import MutedNotice from '../shell/MutedNotice';
import { AppShellLiveContext, AppShellLiveValue } from '../shell/AppShellContext';
import { TelemetryFeedEntry } from '../TelemetryCard';
import CornerOrb from '../shell/CornerOrb';
import RexyOrbThree from '../RexyOrbThree';
import {
  reportNativeCallConnected, endNativeCall, setNativeCallMuted,
  setNativeAudioRoute,
  onNativeCallEnded, onNativeCallEvent,
  isNativeShell, nativeCallRegistered, nativeCallDiagnostics,
} from '../../api/nativeCall';
import {
  announceDocument, mediaEngineCapability, MediaEngineCapability,
  onCallKitStartResult,
} from '../../api/nativeMediaEngine';
import {
  subscribeMediaProbe, formatProbe, probeNoteNative, MediaProbeReading,
  probeNoteRoute, probeNoteRouteFailed,
} from '../../api/mediaProbe';

// The only documents that may ever be loaded in the frame.
const PAGES: Record<string, string> = {
  'Rexy OS Dashboard.dc.html': '/phone/index.html',
  // KEYS are the hrefs the design writes, decoded by guardLinks — design bytes,
  // never renamed. VALUES are the files on disk, renamed 2026-08-03 to drop the
  // spaces: CRA's dev server will not serve a filename containing one. Proven
  // with a one-byte probe — "space probe.txt" 404s, "spaceprobe.txt" 200s — so
  // both documents were unreachable and the CONSOLE and CHAT tabs were dead.
  // Contents are byte-identical through the rename (git: 100% similarity).
  'Rexy OS Console.dc.html': '/phone/rexy-os-console.dc.html',
  'Rexy OS Chat.dc.html': '/phone/rexy-os-chat.dc.html',
};

// The slot in the page's UNSCALED layout units, plus the stage's own unscaled
// size. Visual pixels are derived here — never measured there. See reportSlot():
// getBoundingClientRect() is post-transform and therefore timing-dependent, and
// ResizeObserver cannot see a transform at all, so a visual measurement can
// never be made reliable. Layout units have no such problem.
type SlotLayout = {
  x: number; y: number; w: number; h: number;
  slotW: number; stageW: number; stageH: number;
} | null;
const CALL_READOUTS_SHOWN: boolean = false; // the whole status block across the top: off (owner, 22 Oct 2026). See NOTHING IS DRAWN.
/** One style for both call controls. `on` is the ACTIVE state — speaker engaged,
 *  or microphone muted — and it is always a value the surface was told, never one
 *  it assumed from a tap. */
function callControlStyle(on: boolean): React.CSSProperties {
  return {
    minWidth: 96,
    padding: '9px 16px',
    borderRadius: 999,
    border: `1px solid ${on ? '#2fdcf0' : 'rgba(255,255,255,.22)'}`,
    background: on ? 'rgba(47,220,240,.16)' : 'rgba(255,255,255,.05)',
    color: on ? '#2fdcf0' : 'rgba(255,255,255,.72)',
    font: '600 11px/1 "IBM Plex Mono", ui-monospace, monospace',
    letterSpacing: '.14em',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  };
}

export default function PhoneHost(): React.ReactElement {
  const frame = useRef<HTMLIFrameElement | null>(null);
  const [src, setSrc] = useState<string>(PAGES['Rexy OS Dashboard.dc.html']);
  // TRI-STATE, and the distinction is load-bearing:
  //   undefined → not reported yet (booting, or mid-navigation)
  //   null      → this page HAS no reserved slot (Chat) — show the floating control
  //   object    → this page's slot, in layout units
  //
  // Collapsing the first two into one value is what made every navigation churn
  // the renderer: the parent briefly believed the incoming page was slotless,
  // mounted CornerOrb, then unmounted it and remounted the centre orb. Each
  // mount runs RexyOrbThree's probeWebGL(), which acquires a WebGL context and
  // never releases it, so four contexts leaked per swap. Browsers cap live
  // contexts and evict the OLDEST — which is why the SECOND page loaded was
  // always the degraded one, whichever page it happened to be.
  const [layout, setLayout] = useState<SlotLayout | undefined>(undefined);
  // The last slot we actually had, so the orb can stay MOUNTED (merely hidden)
  // across a swap instead of being torn down and rebuilt.
  const lastSlot = useRef<Exclude<SlotLayout, null>>(null as any);
  // The frame fills the viewport, so the page's window dimensions ARE these.
  const [vp, setVp] = useState({ w: window.innerWidth, h: window.innerHeight });

  // ── BUILD STAMP ────────────────────────────────────────────────────────────
  // The phone loads its front end from the BOX at runtime, not from the app
  // binary, so a TestFlight rebuild proves nothing about the code on screen. On
  // 2026-08-03 that cost a full build cycle: every web-side fix sat in a repo
  // nothing served, and the app looked identical before and after.
  //
  // scripts/copyPhoneCore.js writes this at prestart/prebuild as a HASH OF
  // CONTENT across the eight files that decide what the phone renders. A
  // timestamp would only prove the server restarted; the hash moves if and only
  // if the surface actually changed — so a hash that did NOT move after a "fix"
  // is proof the fix never arrived.
  //
  // cache:'no-store' is the point of the whole thing: a revalidated stamp is
  // worthless if the stamp itself can come from cache.
  const [stamp, setStamp] = useState<{ hash: string; at: string; missing?: number } | null>(null);
  useEffect(() => {
    let live = true;
    fetch('/phone/buildStamp.json', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (live && j && j.hash) setStamp(j); })
      .catch(() => { /* absent stamp renders as nothing, never as a false OK */ });
    return () => { live = false; };
  }, []);

  // ── THIS DOCUMENT SAYS HELLO (F2, stage 3b) ────────────────────────────────
  // Before anything claims the engine, release whatever a PREVIOUS document left
  // holding the microphone. Capacitor reloads the WebView after a web-content
  // crash (WebViewDelegationHandler.webViewWebContentProcessDidTerminate ->
  // bridge.reset() + webView.reload()), so a crash produces a fresh document —
  // but supersede-on-claim only fires when a document starts a call, and a
  // document that never taps never claims. This is the hello that closes that
  // window without hooking WKNavigationDelegate, which Capacitor owns.
  useEffect(() => { announceDocument(); }, []);

  // ── THE CHAIN REPORTS ITSELF (restored 2026-08-07) ────────────────────────
  // Asked ONCE, at mount, before any call. The two booleans are what the JS side
  // BELIEVES; the round-trip is the only thing that proves the native class was
  // found via NSClassFromString off the generated packageClassList, instantiated,
  // and answered. That distinction is the whole diagnosis: it separates "not in
  // the binary" from "there, and CallKit refused".
  //
  // WHY THIS IS BEING RESTORED. It was deleted on 2026-08-05 by 3ca0053, which
  // removed RexyAudioContract and took this effect with it while leaving the
  // readout that renders `callDiag.native` standing. `native` has been false ever
  // since, so THE CALLKIT LINE HAS NOT RENDERED ON ANY BUILD, ON EITHER ARM,
  // SINCE THAT COMMIT. Build 17 was read as evidence about the binary when it was
  // only evidence about a dead gate.
  //
  // MUST STAY PAIRED with the audioActivated/audioDeactivated effect below. This
  // one alone would render `audio not activated` on every call forever — an
  // unearned PESSIMISTIC value, which is read as a finding and is the same fault
  // as an unearned reassuring one.
  useEffect(() => {
    const native = isNativeShell();
    const registered = nativeCallRegistered();
    setCallDiag((d) => ({ ...d, native, registered }));
    if (!native || !registered) {
      setCallDiag((d) => ({ ...d, echo: 'fail', echoErr: 'not registered' }));
      return;
    }
    nativeCallDiagnostics()
      .then((info) => setCallDiag((d) => ({
        ...d,
        echo: 'ok',
        provenance: `provider main=${info.providerBuiltOnMain ? 'y' : 'n'} age=${
          info.providerAgeSeconds}s handle=${info.handleTypeUsed} syscalls=${
          info.systemCalls}`,
      })))
      .catch((e) => setCallDiag((d) => ({
        ...d, echo: 'fail', echoErr: String((e as Error)?.message ?? e).slice(0, 60),
      })));
    // WHAT THE BINARY CARRIED — awaited from announceDocument()'s ONE round trip
    // rather than asked again here. Two calls are two chances to disagree, and
    // this line must show the value the engine SELECTION was made from, not a
    // second opinion about it. The effect above runs first (same component,
    // declared earlier), so the answer is always already in flight by now.
    void mediaEngineCapability().then((capability) => setCallDiag((d) => ({ ...d, capability })));
  }, []);

  // THE SECOND HALF OF THE PAIR. Without it `callDiag.audio` has no setter and the
  // readout says `not activated` through a call that activated perfectly.
  useEffect(() => {
    const handles: Array<{ remove: () => Promise<void> }> = [];
    let cancelled = false;
    const bind = (ev: 'audioActivated' | 'audioDeactivated', on: boolean) => {
      void onNativeCallEvent(ev, () => setCallDiag((d) => ({ ...d, audio: on })))
        .then((h) => { if (h) { if (cancelled) void h.remove(); else handles.push(h); } });
    };
    bind('audioActivated', true);
    bind('audioDeactivated', false);
    // ── THE ROUTE THE BUTTON RENDERS FROM (build 19) ────────────────────────
    // The MEASURED route, never the tap and never this promise resolving. A tap
    // that fails to move the route must leave the button where it was — the same
    // rule as the rest of this surface: never render a value it has not earned.
    // Native supplies `speaker` alongside the raw ports so the surface is not
    // re-deriving a port-type rule the native side already applied.
    void onNativeCallEvent('audioRouteChanged', (d: any) => {
      setSpeakerOn(!!d?.speaker);
      // AND INTO THE READOUT (build 21, Ghost finding 2). `outputVolume` has
      // travelled on this event since build 20 and only `speaker` was stored, so
      // the three-way discriminator that build shipped had no third leg: a call
      // correctly on the speaker at zero system volume is inaudible with every
      // other field healthy, and nothing could say so.
      const vol = Number(d?.outputVolume ?? NaN);
      probeNoteRoute(!!d?.speaker, Number.isFinite(vol) ? vol : null);
    })
      .then((h) => { if (h) { if (cancelled) void h.remove(); else handles.push(h); } });
    // A REFUSED ROUTE IS A FINDING (build 21, Ghost finding 3). Swift has emitted
    // this since the route setter existed and nothing has ever listened. It does
    // NOT move the button — a route that did not move must leave the indicator
    // where it was — it states the failure beside the numbers it explains.
    void onNativeCallEvent('audioRouteFailed', (d: any) => {
      probeNoteRouteFailed(
        `${String(d?.phase ?? 'live')}: ${String(d?.detail ?? 'refused')}`.slice(0, 80),
      );
    })
      .then((h) => { if (h) { if (cancelled) void h.remove(); else handles.push(h); } });
    return () => { cancelled = true; handles.forEach((h) => void h.remove()); };
  }, []);

  // The one app-wide socket singleton — never a second connection.
  //
  // `agents` is relayed as well as vitals/telemetry, and is NOT optional: the
  // Console page derives the whole proximity radar from it —
  //     const radarFailed = noLink || s.connected === false || !s.agents;
  // (Rexy OS Console.dc.html:585). Dropping it here is what made the radar
  // report "NO PROXIMITY DATA — Sensor offline" on the phone while the desktop
  // radar worked: the honest-null was correct, the feed just never arrived.
  const { connected, vitals, telemetry, agents } = useRexySocket();

  // The one voice session on this surface. HOMEPAGE/REXY is her front door, bound
  // atomically as one pair so a tap reaches the persona-loaded Rexy.
  const ptt = useVoiceDaemonPTT({ activeDomain: 'HOMEPAGE', activeAgent: 'REXY' });
  const pttAction = ptt.ptt;
  // ── "AM I IN A CALL" COMES FROM THE BOX (owner ruling 2026-08-05, Ghost
  // countersigned) ───────────────────────────────────────────────────────────
  // Was: `state !== 'idle' && !== 'ending' && !== 'error'` — the surface deciding
  // call existence from PIPELINE HEALTH. A stall sets state='error', so the
  // surface denied a call the box was still holding, and there was then nothing
  // orange to tap. That is how a call ran 95 minutes.
  //
  // `boxCall` is the box's own call_state. `state` still drives warmth, amplitude
  // and transcript — the call's QUALITY — and no longer decides that it exists.
  const inCall = ptt.boxCall != null;
  const active = inCall;

  // The locked VoiceState→warmth map, identical to AppShell's.
  const warmth =
    ptt.state === 'thinking' || ptt.state === 'agent_speaking'
      ? 1.0
      : ptt.state === 'user_speaking'
        ? ptt.userAmplitude * 0.3
        : 0.0;

  // ── THE NATIVE CALL ENGINE — STAGE 1 ─────────────────────────────────────
  // CallKit lifecycle only. The media is still this WebView's WebRTC peer;
  // stage 2 proves the audio session survives backgrounding and stage 3 moves
  // the media native. Everything here is inert off-device.
  //
  // THE RAIL: iOS is told about a call only once the BOX holds one. `connecting`
  // is not enough — the daemon sits there while the offer is negotiated and the
  // box may still refuse. The confirmation is the transition into a live state,
  // which useVoiceDaemon reaches on voice:session_ready or on the transport
  // reporting connected, and on nothing else. So a tap alone never reaches
  // CallKit, and a session the box refused cannot appear on the phone as a call.
  // THE RAIL, NOW STRUCTURAL IN BOTH DIRECTIONS. It used to be a four-state
  // allowlist that happened to agree with the box until the media failed; on a
  // stall it tore the CallKit report down while the box still held the call —
  // the phone denying a call the box has, which is the rail broken in the
  // direction nobody had enforced. Sourced from the box, both directions hold by
  // construction rather than by the allowlist staying in sync.
  //
  // `connecting` still cannot reach CallKit: boxCall is null until the box
  // confirms a call, so a tap alone still never reports one.
  const liveCall = inCall;

  // Reported-to-iOS, tracked separately from `liveCall` so the report happens
  // once per call rather than once per state change within it.
  const reportedToIOS = useRef(false);
  /** The CallKit epoch this surface's call was issued under (Q2), or null when
   *  no call is reported. Stage 3b gates media start on it; at 3a it is carried
   *  and surfaced so the value is provably crossing the bridge before anything
   *  depends on it. */
  // Set only once the CXStartCallAction transaction has actually RESOLVED, so
  // connectedAt cannot be reported against a call CallKit does not hold yet.
  // The two facts arrive from unrelated sources and either order is possible:
  // if media connects while the transaction is still in flight, this flipping
  // is what fires the connected report instead.
  const [iosCallStarted, setIosCallStarted] = useState(false);
  /** What the route MEASURED, seeded from the constant the call starts on.
   *  Corrected by the first `audioRouteChanged`, which arrives as soon as the
   *  category takes effect — so a seed that turned out wrong is visibly wrong
   *  rather than durably wrong. */
  const [speakerOn, setSpeakerOn] = useState(true);

  // ── THE READOUT ───────────────────────────────────────────────────────────
  // Build 10 shipped and told nobody anything: no lock screen, no transition,
  // no error — and no way to tell whether the plugin was even in the binary.
  // An hour of guessing followed, which is an hour more than the fact was
  // worth. Every link in the chain now reports itself where the owner can read
  // it, on the same principle as the build stamp and the VOICE error line: if
  // it fails, it says so on screen rather than being inferred from silence.
  const [callDiag, setCallDiag] = useState<{
    native: boolean; registered: boolean;
    echo: 'pending' | 'ok' | 'fail'; echoErr: string;
    start: 'idle' | 'requested' | 'ok' | 'rejected'; startErr: string;
    connected: boolean; audio: boolean; endErr: string;
    // WHAT THE BINARY CARRIED. `null` until the plugin has answered — and it stays
    // null when the plugin never answers at all, because the `echo: 'fail'` branch
    // already says PLUGIN NOT IN BINARY and a capability verdict restating it
    // would be a second claim from the same silence.
    capability: MediaEngineCapability | null;
    // Provider provenance, read once at mount. Only shown when something has
    // gone wrong, because it is diagnostic weight and not a status.
    provenance: string;
  }>({
    native: false, registered: false,
    echo: 'pending', echoErr: '',
    start: 'idle', startErr: '',
    connected: false, audio: false, endErr: '',
    capability: null,
    provenance: '',
  });

  // THE DEVICE HALF OF THE STALL, ON SCREEN. The box can only report frames that
  // arrived; this reports what the device did before one did. It renders in a
  // NATIVE SHELL AND IN A BROWSER ALIKE — deliberately, because "was that reading
  // the phone or the desktop?" is the exact question the audit log could not
  // answer about the 3.3s baseline, and an instrument that only runs on one
  // surface would leave the next comparison just as ambiguous.
  const [probe, setProbe] = useState<MediaProbeReading | null>(null);
  useEffect(() => subscribeMediaProbe(setProbe), []);

  // THE AUDIO CONTRACT, READ AT THE MOMENT THE MICROPHONE OPENS — not at mount.
  // `manualAudio` / `audioEnabled` are the two flags the suppression hypothesis
  // turns on, and they belong ON the same line as the samples they are supposed
  // to explain. Read when the probe records the mic and not before, because a
  // value captured at mount and shown beside a later silence is exactly the
  // provenance mistake build 12 already cost a cycle to.
  // Re-read at the mic AND again once the reading closes. The mic-open snapshot
  // is taken BEFORE the `call` arm's own touch happens, so on that arm it will
  // always say `session untouched` — true when taken, and misleading if it is the
  // only reading shown beside a call that went on to activate audio.
  const notedNativeFor = useRef<string>('');
  useEffect(() => {
    if (!probe || probe.micMs === null || !isNativeShell()) return;
    const phase = probe.firstRtpMs !== null || probe.gaveUp || probe.closedMs !== null
      ? 'end' : 'mic';
    const key = `${probe.startedAt}:${phase}`;
    if (notedNativeFor.current === key) return;
    notedNativeFor.current = key;
    const forStartedAt = probe.startedAt;
    void nativeCallDiagnostics()
      .then((info) => {
        // `untouched` IS NOT `false`. Rendering it as `manual=n` would say the
        // session was contacted and reported no manual audio, when in fact it was
        // never contacted — the exact blur the arms exist to remove.
        const flag = (v: boolean | 'untouched') => (v === 'untouched' ? '?' : v ? 'y' : 'n');
        const flags = info.audioFlagsSource === 'untouched'
          ? 'session untouched'
          : `manual=${flag(info.manualAudio)} en=${flag(info.audioEnabled)}`;
        probeNoteNative(flags, forStartedAt);
      })
      .catch(() => { /* the probe stands on its own measurements */ });
  }, [probe]);

  // ── THE ARM SWITCH ────────────────────────────────────────────────────────
  // The WebView's URL is fixed by capacitor.config.ts server.url, so there is no
  // query string to edit on a phone and no console to type into. The readout
  // itself is the control: tap it to cycle off -> call -> launch. That is why
  // this one band takes pointer events while the rest of the stack does not.
  //
  // It reports what it stored AND that a relaunch is needed, because the arm
  // under test is a launch-time behaviour and a switch that silently did nothing
  // until the next cold start is exactly the kind of quiet lie that costs a
  // TestFlight cycle to notice.
  // ── ENDING IS NOT DONE UNTIL iOS AGREES (Ghost, 2026-08-04) ──────────────
  // This used to clear both flags and then fire endNativeCall() with the
  // failure swallowed. If the CXEndCallAction transaction rejected — which
  // RexyCallController.end() surfaces as callkit_end_failed — iOS went on
  // showing a call the box no longer held, while the surface had already
  // recorded it gone and the readout said `start idle`. That is the same rail
  // this stage exists to hold, running in the other direction: a call state on
  // the phone that the box does not have.
  //
  // So nothing is cleared until the end actually succeeds. On failure it
  // retries with backoff and, if it still cannot clear, keeps reportedToIOS
  // TRUE — the surface must not claim iOS is clear when it is not — and says
  // so loudly on screen, because a phantom call is exactly the thing the owner
  // cannot otherwise see.
  const endNativeRef = useRef<(attempt: number) => void>(() => {});
  /** WE ended it, so the `ended` callback that follows is OUR OWN ECHO and must
   *  not be relayed back as if the owner had tapped the lock screen. This
   *  replaces the state allowlist that used to serve the same purpose — see the
   *  ended listener below for why that allowlist was the wrong instrument. */
  const weEndedNatively = useRef(false);
  endNativeRef.current = (attempt: number) => {
    weEndedNatively.current = true;
    void endNativeCall()
      .then(() => {
        reportedToIOS.current = false;
        setIosCallStarted(false);
        setCallDiag((d) => ({
          ...d, start: 'idle', startErr: '', connected: false, endErr: '',
        }));
      })
      .catch((e) => {
        const msg = String((e as Error)?.message ?? e).slice(0, 60);
        if (attempt < 3) {
          setCallDiag((d) => ({ ...d, endErr: `${msg} (retry ${attempt + 1}/3)` }));
          setTimeout(() => endNativeRef.current(attempt + 1), 400 * (attempt + 1));
          return;
        }
        // Out of retries. iOS still holds a call the box does not, and the only
        // honest thing left is to say so and stop pretending otherwise.
        setCallDiag((d) => ({ ...d, endErr: `${msg} — iOS STILL HOLDS A CALL` }));
      });
  };

  // ── AN ADOPTED CALL SAYS SO, AND SAYS HOW LONG ──────────────────────────────
  // Owner ruling 2026-08-05. Adoption is correct: a call is live on the box, so
  // every window shows it. What was missing is that an adopted call looked
  // identical to one just started — so 45865a8d ran 95 minutes and 98f34fe3 ran
  // unnoticed. That is a missing CONTROL, not a missing timer, and NOTHING BUT A
  // TAP ENDS A CALL still holds absolutely: this line ends nothing, it only makes
  // the call visible enough to be tapped with intent.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (ptt.boxCall == null) return;
    // Only while an adopted call is on screen, and only once a second: a ticking
    // clock that runs when nothing is shown is a render loop with no reader.
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [ptt.boxCall]);

  // DURATION COMES FROM THE BOX'S CALL, not from the adoption record — so a call
  // this window started shows how long it has been running too. `since` is on
  // every call_state (voice_b_ws.py:285), not only on adopted ones.
  const callFor = useMemo(() => {
    const since = ptt.boxCall?.since;
    if (!since) return null;
    // The box sends seconds. A future timestamp means the clocks disagree, and
    // "0s" is a truer answer than a negative one.
    const secs = Math.max(0, Math.round(now / 1000 - since));
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${secs % 60}s` : `${secs}s`;
  }, [ptt.boxCall, now]);

  const pttStateRef = useRef(ptt.state);
  pttStateRef.current = ptt.state;
  /** The box's call, readable from a listener that is mounted once. Closing over
   *  `ptt.boxCall` under `[]` deps would capture the mount-time value — null,
   *  always — and the lock-screen relay would then read "nothing to end" on
   *  every call. */
  const pttBoxCallRef = useRef(ptt.boxCall);
  pttBoxCallRef.current = ptt.boxCall;
  const pttActionRef = useRef(pttAction);
  pttActionRef.current = pttAction;

  // ── PHONEHOST OBSERVES; IT NO LONGER REPORTS (build 19, Ghost round 4) ────
  //
  // This effect used to CALL startNativeCall itself, alongside the engine's own
  // call in nativeMediaEngine.open(). Two reporters, and the controller's
  // idempotence began at `callId` — which is assigned only in the transaction
  // completion — so both could enter the window before it existed, each bump the
  // epoch, and leave the first one's completion stale with no activation armed.
  // Media then opened against a disabled session with nothing coming, and the
  // deferral that would have made the stall visible was disarmed by the same nil.
  //
  // The engine's is the reporter that survives, and not by preference: it is the
  // one that can be AWAITED before startMedia, which is the whole of the 3c
  // ordering. This effect cannot fire until `boxCall` resolves — after the
  // sequence has already begun — which is exactly why the report moved out of it.
  //
  // ── AND THE ARM IS GONE ────────────────────────────────────────────────────
  // What stood here was the A/B's arm-B gate. Its evidence, kept because it is
  // evidence and not scaffolding: on 2026-08-06 two calls ran the same code and
  // disagreed — `session untouched` gave samples 3.27s, `manual=y en=y` gave
  // 0.00s with "Failed to start the audio device" and the box seeing no RTP. The
  // variable was whether CallKit took the audio session: one AVAudioSession, two
  // owners. Arm B was also Apple's own recommended configuration for
  // foreground-only calling (DTS thread 767949), so it was a real operating mode
  // rather than a scaffold — which is why it was a named arm.
  //
  // It is DELETED rather than flipped. `callKitArm()` read localStorage BEFORE
  // the source constant, so a persisted `suppressed` outranked the flip: build 19
  // could have shipped complete, with a complete record, and reported nothing.
  // A value can be cleared and set again; a reader that does not exist cannot be.
  // Returning to foreground-only calling is now a commit, not a setting.
  useEffect(() => {
    const unsubscribe = onCallKitStartResult((r) => {
      if (r.state === 'requested') {
        reportedToIOS.current = true;
        setCallDiag((d) => ({ ...d, start: 'requested', startErr: '' }));
        return;
      }
      if (r.state === 'ok') {
        setIosCallStarted(true);
        setCallDiag((d) => ({ ...d, start: 'ok' }));
        return;
      }
      // iOS REFUSED. Do not go on believing there is a CallKit call — the rail
      // runs in this direction too. The voice session is untouched and still
      // works; only the system-call presentation failed. A rejected transaction
      // used to clear the flag SILENTLY, which looks on screen exactly like a
      // plugin that was never there. It now says which.
      reportedToIOS.current = false;
      setIosCallStarted(false);
      setCallDiag((d) => ({ ...d, start: 'rejected', startErr: r.error }));
      // RE-READ PROVENANCE AT THE MOMENT OF FAILURE. Captured once at mount it
      // read 0s — the mount-time diagnostics call is itself what built the
      // provider — and was then shown next to a rejection that happened much
      // later. Read here, the numbers describe the failure.
      void nativeCallDiagnostics()
        .then((info) => setCallDiag((d) => ({
          ...d,
          provenance: `provider main=${info.providerBuiltOnMain ? 'y' : 'n'} age=${
            info.providerAgeSeconds}s handle=${info.handleTypeUsed} syscalls=${
            info.systemCalls}`,
        })))
        .catch(() => {});
      console.warn('[nativeCall] CallKit refused the call:', r.error);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    // `connecting` is deliberately not a teardown: nothing was reported yet.
    if (!liveCall && ptt.state !== 'connecting' && reportedToIOS.current) {
      endNativeRef.current(0);
    }
  }, [liveCall, ptt.state]);

  // CONNECTED IS A SECOND, LATER FACT. session_ready above means the box
  // accepted the session; this means the PEER is established and media can
  // flow (voiceDaemon.ts:525, surfaced as mediaConnected). Reporting connected
  // off session_ready would have iOS assert something the box has not
  // confirmed — the one thing this rail forbids. If the gap is microseconds
  // nobody sees the connecting state; if it ever is not, iOS tells the truth.
  useEffect(() => {
    if (!iosCallStarted || !ptt.mediaConnected) return;
    void reportNativeCallConnected()
      .then(() => setCallDiag((d) => ({ ...d, connected: true })))
      .catch(() => {});
  }, [ptt.mediaConnected, iosCallStarted]);

  // The mute state iOS shows and the mute state the box holds are one thing.
  useEffect(() => {
    if (!reportedToIOS.current) return;
    void setNativeCallMuted(ptt.muted).catch(() => {});
  }, [ptt.muted]);

  // Ended from the CallKit UI. STAGE 1 SCOPE: this relays through the existing
  // teardown, which reaches the box over the socket the WebView still holds —
  // alive because there is no background mode in this round. Lock-screen end is
  // NOT claimed at stage 1; it needs a native path back to the box, and that is
  // stage 3's DataChannel.
  //
  // Guarded on the session still being live, because OUR OWN endNativeCall()
  // also lands here. Without the guard, ending a call would fire the toggle
  // again and start a new one.
  useEffect(() => {
    let handle: { remove: () => Promise<void> } | null = null;
    let cancelled = false;
    void onNativeCallEnded(() => {
      // ── OUR OWN ECHO, TOLD APART BY A FLAG (Ghost round 6) ────────────────
      // endNativeCall() lands here too, and relaying that would end the call
      // twice — or, from idle, START one. That was the reason for the guard;
      // the guard was just built out of the wrong material.
      if (weEndedNatively.current) {
        weEndedNatively.current = false;
        return;
      }
      // ── THE OWNER ENDED FROM THE LOCK SCREEN ──────────────────────────────
      // This was a FOUR-STATE ALLOWLIST — listening / user_speaking / thinking /
      // agent_speaking — which excluded `connecting`. And `connecting` is
      // precisely the pre-channel window: CallKit has confirmed a call, media is
      // deferred awaiting the audio session, and sendUserEnd() has nothing to
      // send on because the control channel is not created until negotiation
      // begins. So the native end reached nothing AND the socket relay was
      // skipped, and the box kept a call the owner had ended.
      //
      // It is the same defect class as the original rail break: a health
      // allowlist standing in for the fact. The fact is "is there anything to
      // end", and it is asked the way the tap gate asks it — so this can never
      // START a call, which is the only thing the allowlist was protecting
      // against.
      const nothingToEnd = pttBoxCallRef.current == null && pttStateRef.current === 'idle';
      if (nothingToEnd) return;
      pttActionRef.current();
    }).then((h) => {
      if (cancelled) void h?.remove();
      else handle = h;
    });
    return () => {
      cancelled = true;
      void handle?.remove();
    };
    // boxCall is read through pttBoxCallRef, NOT closed over, so this stays
    // mounted once — re-subscribing on every call-state change would drop
    // lock-screen ends in the gap between removing one listener and adding the
    // next.
  }, []);

  // Rolling telemetry window with stable ids, for context parity with AppShell.
  const [feed, setFeed] = useState<TelemetryFeedEntry[]>([]);
  const seq = useRef(0);
  useEffect(() => {
    if (!telemetry || !telemetry.event) return;
    setFeed((prev) => {
      const head = prev[0];
      if (head && head.time === telemetry.time && head.event === telemetry.event
          && head.status === telemetry.status) return prev;
      return [{ ...telemetry, id: `tl${seq.current++}` }, ...prev].slice(0, 30);
    });
  }, [telemetry]);

  const post = useCallback((msg: any) => {
    const win = frame.current && frame.current.contentWindow;
    if (win) win.postMessage(msg, window.location.origin);
  }, []);

  // ── SAFE-AREA INSETS, MEASURED HERE AND RELAYED ──────────────────────────
  // env(safe-area-inset-*) resolves against the TOP-LEVEL viewport and only when
  // that document carries viewport-fit=cover — which THIS one does
  // (public/index.html:5) and the framed document does not. Inside the iframe
  // env() reads 0 regardless of the notch, so the measurement has to happen out
  // here and travel in, exactly like the socket relay.
  //
  // Measured by probe rather than hard-coded: 59/34 is an iPhone 15, not a fact
  // about every device, and a wrong constant would put the home indicator over
  // the clock bar.
  const readInsets = useCallback(() => {
    const probe = document.createElement('div');
    probe.style.cssText =
      'position:fixed;top:0;left:0;width:0;height:0;visibility:hidden;pointer-events:none;' +
      'padding-top:env(safe-area-inset-top);padding-right:env(safe-area-inset-right);' +
      'padding-bottom:env(safe-area-inset-bottom);padding-left:env(safe-area-inset-left)';
    document.body.appendChild(probe);
    const cs = window.getComputedStyle(probe);
    const v = {
      top: parseFloat(cs.paddingTop) || 0,
      right: parseFloat(cs.paddingRight) || 0,
      bottom: parseFloat(cs.paddingBottom) || 0,
      left: parseFloat(cs.paddingLeft) || 0,
    };
    probe.remove();
    return v;
  }, []);

  useEffect(() => {
    const send = () => post({ type: 'rexy:insets', insets: readInsets() });
    send();
    window.addEventListener('resize', send);
    window.addEventListener('orientationchange', send);
    return () => {
      window.removeEventListener('resize', send);
      window.removeEventListener('orientationchange', send);
    };
  }, [post, readInsets, src]);

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      const win = frame.current && frame.current.contentWindow;
      if (!win || e.source !== win) return;
      const data = e.data;
      if (!data || typeof data.type !== 'string') return;

      if (data.type === 'rexy:phone-ready') {
        post({ type: 'rexy:token', token: (window as any).__rexyToken || '' });
        post({ type: 'rexy:live', connected, vitals, telemetry, agents });
        // Insets go with the handshake too: the periodic sender fires on resize
        // and orientation, but a page that mounts between those events would
        // otherwise lay out at inset 0 and put content under the notch until the
        // first rotation.
        post({ type: 'rexy:insets', insets: readInsets() });
        return;
      }
      if (data.type === 'rexy:navigate') {
        const next = PAGES[String(data.page || '')];
        // A page swap invalidates the previous page's slot until the new one
        // reports — never carry a stale measurement across a navigation.
        // Clear the published measurement so a stale one can never be rendered
        // against the new page, even for a frame — but clear it to UNKNOWN, not
        // to "slotless", so the renderer is not torn down and rebuilt.
        if (next) { setLayout(undefined); setSrc(next); }
        return;
      }
      if (data.type === 'rexy:slot') {
        const l = data.layout;
        const ok = l && l.w > 0 && l.h > 0 && l.stageW > 0 && l.stageH > 0;
        setLayout(ok ? l : null);
        return;
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [post, connected, vitals, telemetry, agents]);

  useEffect(() => {
    post({ type: 'rexy:live', connected, vitals, telemetry, agents });
  }, [post, connected, vitals, telemetry, agents]);

  // The scale is a pure function of the viewport, so the overlay follows a
  // resize or rotation here without the page having to re-measure anything.
  useEffect(() => {
    const onResize = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  const liveValue: AppShellLiveValue = useMemo(
    () => ({
      connected,
      vitals,
      feed,
      meter: ptt.meter,
      warmth,
      active,
      state: ptt.state,
      userAmplitude: ptt.userAmplitude,
      agentAmplitude: ptt.agentAmplitude,
      ptt: pttAction,
      error: ptt.error,
      muted: ptt.muted,
      mutedStandingS: ptt.mutedStandingS ?? 0,
      toggleMute: ptt.toggleMute,
      partialTranscript: ptt.partialTranscript,
      agentText: ptt.agentText,
      // The per-agent call surface — owner-approved context widening, so a
      // button on an agent's page reaches THE SINGLE daemon client rather than
      // constructing a second one.
      start: ptt.start ?? null,
      boxCall: ptt.boxCall ?? null,
      adopted: ptt.adopted ?? null,
      daemonBinding: ptt.daemonBinding ?? null,
      // The phone surface frames design documents and has no agent navigation of
      // its own; it is Rexy's. Stated rather than left undefined.
      navDomain: 'HOMEPAGE',
      navAgent: null,
    }),
    [connected, vitals, feed, ptt.meter, warmth, active, ptt.state, ptt.userAmplitude,
     ptt.agentAmplitude, pttAction, ptt.error, ptt.muted, ptt.toggleMute,
     ptt.partialTranscript, ptt.agentText,
     ptt.start, ptt.boxCall, ptt.adopted, ptt.daemonBinding],
  );

  // ── centre orb sizing ──────────────────────────────────────────────────────
  // The VISIBLE orb is only a fraction of its canvas, which is why sizing the
  // canvas to the gap still read small. RexyOrbThree.tsx:85-87 fixes the framing:
  // a PerspectiveCamera at fov 50 sitting at z=3.5, with the shell at unit radius.
  // The frame height at the orb's plane is 2·tan(fov/2)·z, so the orb's diameter
  // covers 2 / (2·tan(25°)·3.5) ≈ 0.613 of the canvas.
  //
  // Those three numbers are RexyOrbThree's, and its camera is a FROZEN BLOCK, so
  // they are mirrored here rather than changed there. If the camera is ever
  // unfrozen, this must move with it — that coupling is the price of not
  // touching the renderer.
  const ORB_FOV_DEG = 50;
  const ORB_CAM_Z = 3.5;
  const ORB_SHELL_RADIUS = 1;
  const VISIBLE_FRACTION =
    (2 * ORB_SHELL_RADIUS) / (2 * Math.tan((ORB_FOV_DEG * Math.PI) / 180 / 2) * ORB_CAM_Z);

  // Clearance so the orb CLEARS both columns rather than touching them: the
  // visible orb takes 92% of the gap, leaving a 4% margin either side. A
  // fraction, not a pixel value — the stage is scaled to fit, so a fixed pixel
  // gap would be wrong at every other viewport.
  const COLUMN_CLEARANCE = 0.92;

  // THE SCALE IS DERIVED HERE, from the stage's unscaled size and the viewport.
  // This is byte-for-byte the page's own measure(): fit = clamp(min(w/W, h/H)).
  // Because it is computed rather than observed, it cannot be read "too early" —
  // there is no moment at which it is wrong.
  const scale = layout
    ? Math.max(0.2, Math.min(1, Math.min(vp.w / layout.stageW, vp.h / layout.stageH)))
    : 0;
  // The stage is centred in a fixed inset:0 flex container with
  // transform-origin:center, so the scaled box is centred on the same point.
  const originX = layout ? (vp.w - layout.stageW * scale) / 2 : 0;
  const originY = layout ? (vp.h - layout.stageH * scale) / 2 : 0;

  // THE LETTERBOX BAND, MEASURED — not assumed. The stage is centred, so the band
  // below it is exactly `originY` tall; when the page fills the viewport (the
  // 2026-08-03 fill: stageH = vh / fit) that is 0 and there is NO band to render
  // host chrome into. Anything anchored to the bottom then lands on the domain
  // strip and the clock bar.
  //
  // The floor is the stack's own worst case: four rows at 10px on 1.45 plus the
  // gaps, so a band that cannot hold the stack is treated as no band rather than
  // letting it spill over the stage. Derived from the numbers it depends on, so
  // it stays true if the type is ever re-set.
  const DIAG_ROWS = 4;
  const DIAG_BAND_MIN = DIAG_ROWS * 10 * 1.45 + (DIAG_ROWS - 1) * 5 + 10;
  const diagDockTop = originY < DIAG_BAND_MIN;

  const liveSlot = layout
    ? {
        x: originX + layout.x * scale,
        y: originY + layout.y * scale,
        w: layout.w * scale,
        h: layout.h * scale,
      }
    : null;
  if (liveSlot) lastSlot.current = liveSlot as any;

  // Known-slotless (Chat) is the ONLY case that shows the floating control.
  const pageIsSlotless = layout === null;
  const navigating = layout === undefined;
  // Keep the geometry across the unknown window so the orb stays mounted; it is
  // hidden, so no stale measurement is ever visible.
  const slot: { x: number; y: number; w: number; h: number } | null =
    liveSlot || (navigating ? (lastSlot.current as any) : null);

  // The VISIBLE orb spans (almost all of) the gap between the columns; the
  // canvas is grown to suit and simply overflows, which is free because it is
  // transparent outside the orb. Height is the only hard ceiling — the canvas
  // stays square and must still fit the reserved rectangle's height.
  const visibleTarget = slot ? Math.min(slot.w * COLUMN_CLEARANCE, slot.h) : 0;
  const centreSize = slot
    ? Math.min(visibleTarget / VISIBLE_FRACTION, slot.h)
    : 0;
  // What the user actually sees, and therefore the only thing that may be
  // clickable. A rectangular target the size of the canvas would sit over both
  // panel columns and steal their clicks.
  const visibleSize = centreSize * VISIBLE_FRACTION;

  return (
    <AppShellLiveContext.Provider value={liveValue}>
      <iframe
        ref={frame}
        title="Rexy OS — phone surface"
        src={src}
        style={{
          position: 'fixed', inset: 0, width: '100%', height: '100%',
          border: 'none',
          // The design owns every pixel inside the frame, including its own
          // background. This matches the documents' canvas so the letterbox
          // around the fixed 941×1672 stage is not a bright seam.
          background: '#01050c',
        }}
      />

      {/* NOTHING IS DRAWN ACROSS THE TOP (owner, 22 Oct 2026: the readouts "were for a
          TTS build that has long been finished"; "this text at the top completely needs
          to go"). The whole stack below (the readouts, the VOICE error line, the muted
          row) is drawn only when CALL_READOUTS_SHOWN is true. The data is still collected,
          and the box still records the daemon's errors and the phone's mutes in the call's
          own file. The MUTE control and the call itself are untouched. */}
      {/* ── THE DIAGNOSTIC STACK ────────────────────────────────────────────────
          MEDIA, CALLKIT and the build stamp were three separately-positioned
          fixed elements at hard-coded offsets 31px / 18px / 5px apart. At 9px
          type that fits only while every line stays one line — and the CallKit
          line wraps the moment it carries an error, so it climbed straight over
          the MEDIA line and the owner could read neither.

          They are now ONE bottom-anchored column: the flow keeps them apart, so
          any line may wrap to any height and the stack simply grows upward.
          Order is deliberate — MEDIA on top, CALLKIT beneath it, stamp last —
          because that is the order they are read in when a call fails.

          Type is 10px on 1.45 with the letter-spacing eased off: this is
          evidence read on a phone at arm's length, and it was set at diagram
          scale rather than reading scale.

          ── WHERE IT SITS (2026-08-06) ──────────────────────────────────────
          This stack was anchored to `bottom: safe-area + 5px` on the premise
          that the fixed 941x1672 stage was letterboxed and a black band sat
          below the clock bar. THE FILL RULING OF 2026-08-03 ABOLISHED THAT
          BAND: rexy-os-console.dc.html measure() sets stageH = vh / fit, so
          stageH * fit === vh exactly and the vertical letterbox is ZERO. The
          band the stack was written to live in stopped existing, and the stack
          — the amber MEDIA line and a full-width RED voice-error line — went
          straight over the domain strip and the clock bar, obscuring the two
          rows the owner needs to use.

          So the position is DERIVED from the band rather than assumed. The host
          already computes it: `originY` IS the band height. Where a real band
          exists (landscape, iPad — measure() floors at the drawn height and
          letterboxes) the stack sits in it, as intended. Where there is none it
          docks to the TOP under the notch, over the header row rather than over
          the controls, on a backdrop dark enough to read against the design.
          pointer-events stays off either way: this is evidence, never a
          target. */}
      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          ...(diagDockTop
            ? { top: 'calc(env(safe-area-inset-top, 0px) + 2px)' }
            : { bottom: 'calc(env(safe-area-inset-bottom, 0px) + 5px)' }),
          zIndex: 7,
          pointerEvents: 'none',
          display: CALL_READOUTS_SHOWN ? 'flex' : 'none',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 5,
          padding: diagDockTop ? '5px 10px 6px' : '0 10px',
          background: diagDockTop ? 'rgba(1,5,12,.88)' : undefined,
          fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
          fontSize: 10,
          lineHeight: 1.45,
          textAlign: 'center',
        }}
      >
      {/* MUTED — FIRST ROW, ABOVE THE ERROR (Fault 2, 2026-08-31). A muted
          microphone is not an error and never produced one: the box drops the
          audio before an utterance can open, so every downstream instrument
          reads a quiet room. It sits above VOICE because it is the reason there
          is nothing else to read. */}
      <MutedNotice muted={ptt.muted} standingS={ptt.mutedStandingS ?? 0} compact />

      {/* VOICE ERROR — now a ROW OF THIS STACK rather than a fourth fixed
          element at its own hard-coded offset. It was left out when the other
          three were consolidated, which is how a wrapping red line came to sit
          across the clock bar on its own. The stack's whole point is that the
          flow keeps the lines apart; a line outside it is a line that can
          collide with everything in it. */}
      {ptt.error ? (
        <div style={{ letterSpacing: '.06em', wordBreak: 'break-word', color: '#ef4444' }}>
          VOICE — {String(ptt.error)}
        </div>
      ) : null}

      {/* MEDIA PROBE — same rule as every readout here: an unmeasured field reads
          `—`, never a reassuring zero. Amber once the first RTP packet is later
          than the box's 3.3s baseline, red if it never came. Shown on every
          surface, unlike the CallKit line, because it is the answer to a question
          about WHICH surface produced a reading. */}
      {/* THE ADOPTED CALL. Amber, at the top of the stack, in the owner's words
          rather than the system's: this call was already running when the app
          opened, here is how long, and the sphere ends it as it always has. */}
      {/* RENDERS ON THE BOX'S CALL — the second question, per the ruling. The
          wording is the ONLY thing `adopted` and `error` decide, and none of the
          three says anything untrue: a healthy call this window started reads
          plainly as a call, not as trouble it is not in. */}
      {CALL_READOUTS_SHOWN && inCall ? (
        <div
          style={{
            letterSpacing: '.04em',
            wordBreak: 'break-word',
            color: ptt.state === 'error' ? '#ef4a4a' : '#f59e0b',
          }}
        >
          {ptt.state === 'error'
            ? 'CALL IN TROUBLE'
            : ptt.adopted ? 'ALREADY IN A CALL' : 'IN A CALL'}
          {callFor ? ` · RUNNING ${callFor}` : ''} · TAP THE SPHERE TO END
        </div>
      ) : null}

      {CALL_READOUTS_SHOWN && probe ? (
        <div
          style={{
            letterSpacing: '.04em',
            wordBreak: 'break-word',
            color:
              probe.gaveUp && probe.firstRtpMs === null
                ? '#ef4a4a'
                : probe.firstRtpMs === null || probe.firstRtpMs > 3500
                  ? '#f59e0b'
                  : '#3d5566',
          }}
        >
          {formatProbe(probe)}
        </div>
      ) : null}

      {/* CALLKIT READOUT — it never renders a reassuring value it has not earned.
          Amber is any state that is not a working call, so a glance is enough.
          Shown only in a native shell: in a desktop browser there is no CallKit
          and an absent line is the honest answer, not a failure. */}
      {CALL_READOUTS_SHOWN && callDiag.native ? (
        <div
          style={{
            letterSpacing: '.04em',
            wordBreak: 'break-word',
            color:
              callDiag.echo !== 'ok' || callDiag.start === 'rejected' || callDiag.endErr
                ? '#ef4a4a'
                : callDiag.connected && callDiag.audio
                  ? '#3d5566'
                  : '#f59e0b',
          }}
        >
          {callDiag.echo === 'pending'
            ? 'CALLKIT · asking native…'
            : callDiag.echo === 'fail'
              ? `CALLKIT · PLUGIN NOT IN BINARY${
                  callDiag.registered ? ' (registered, no answer)' : ' (not registered)'
                }${callDiag.echoErr ? ` · ${callDiag.echoErr}` : ''}`
              : `CALLKIT · plugin ok · ${
                  // WHAT THE BINARY CARRIED — and an ABSENT key is never phrased
                  // as a negative ANSWER (Ghost, 2026-08-07). A pre-3b binary does
                  // not say "no": the key did not exist when it was built, so it
                  // says nothing, and `engine no` there would invent a reply it
                  // never made. `NOT IN BINARY` is a statement about the binary.
                  // `answered no` is reserved for a binary that actually said
                  // false — which none built from this source can, and which is
                  // exactly why it needs its own words rather than sharing.
                  callDiag.capability === 'carried'
                    ? 'engine yes'
                    : callDiag.capability === 'absent'
                      ? 'engine NOT IN BINARY'
                      : callDiag.capability === 'declined'
                        ? 'engine answered no'
                        : 'engine —'
                } · start ${callDiag.start}${
                  callDiag.startErr ? `: ${callDiag.startErr}` : ''
                } · connected ${callDiag.connected ? 'yes' : 'no'} · audio ${
                  callDiag.audio ? 'active' : 'not activated'
                }${callDiag.endErr ? ` · END FAILED: ${callDiag.endErr}` : ''}${
                  (callDiag.start === 'rejected' || callDiag.endErr) && callDiag.provenance
                    ? ` · ${callDiag.provenance}`
                    : ''
                }`}
        </div>
      ) : null}

      {CALL_READOUTS_SHOWN && stamp ? (
        <div
          style={{
            letterSpacing: '.10em',
            color: stamp.missing ? '#f59e0b' : '#3d5566',
          }}
        >
          {stamp.hash}
          {stamp.missing ? ` · ${stamp.missing} MISSING` : ''}
        </div>
      ) : null}
      </div>

      {/* The centre orb stays MOUNTED across a page swap — hidden, never torn
          down — so RexyOrbThree's probeWebGL() runs once rather than on every
          navigation. CornerOrb appears only for a page that has explicitly
          reported it has no slot. */}
      {slot && !pageIsSlotless ? (
        // Composited ABOVE the reserved centre. The slot below keeps its own
        // pointer-events:none and stays empty — this overlay is a sibling, never
        // a child of the template.
        <div
          style={{
            position: 'fixed',
            left: slot.x + (slot.w - centreSize) / 2,
            top: slot.y + (slot.h - centreSize) / 2,
            width: centreSize,
            height: centreSize,
            zIndex: 4,
            // The canvas is WIDER than the gap on purpose and overhangs both
            // columns. It is transparent there, but it must never intercept a
            // click meant for a panel — so the box itself is inert and only the
            // circular target below is live.
            pointerEvents: 'none',
            // Hidden, not unmounted, while the incoming page has yet to report.
            visibility: navigating ? 'hidden' : 'visible',
          }}
        >
          <RexyOrbThree
            size={centreSize}
            userAmplitude={ptt.userAmplitude}
            agentAmplitude={ptt.agentAmplitude}
            warmth={warmth}
            sessionActive={active}
            idleFormation="rexy"
            silhouetteConfiguration="fixed"
          />
          {/* The call button: a circle the size of the VISIBLE orb, centred and
              nothing larger. Tap starts and ends the call, as on every surface. */}
          <div
            role="button"
            aria-label="Rexy — tap to talk"
            title="Tap to talk to Rexy"
            onClick={pttAction}
            style={{
              position: 'absolute',
              left: (centreSize - visibleSize) / 2,
              top: (centreSize - visibleSize) / 2,
              width: visibleSize,
              height: visibleSize,
              borderRadius: '50%',
              pointerEvents: 'auto',
              cursor: 'pointer',
            }}
          />
          {/* ── SPEAKER AND MUTE (build 19) ───────────────────────────────────
              GATED ON `boxCall`, WHICH IS THE ONLY AUTHORITY ON EXISTENCE. Neither
              control may create or imply a call, and neither may end one — nothing
              but a tap on the sphere ends a call.

              Both render from state they did not set: the route from the MEASURED
              route event, mute from the box's echo (`ptt.muted`, which
              `voice:mute_state` is the only writer of). A tap sends and then waits;
              it never paints its own outcome. That is what makes a control that
              failed silently impossible to mistake for one that worked.

              THE MUTE BUTTON USES THE SOCKET FUNNEL, NOT CallKit. Three inputs,
              ONE box-owned state: the lock screen relays over the native control
              channel (the only route that survives backgrounding), and a
              foregrounded window — this one, or any other — uses the socket it
              already holds. Both land on the same box mute, never a parallel
              native-only one. Routing this button through CallKit instead would
              make the foreground path depend on a system UI it does not need. */}
          {inCall ? (
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: centreSize / 2 + visibleSize / 2 + 18,
                display: 'flex',
                justifyContent: 'center',
                gap: 14,
                pointerEvents: 'auto',
              }}
            >
              <button
                type="button"
                aria-label={speakerOn ? 'Speaker on' : 'Speaker off'}
                aria-pressed={speakerOn}
                onClick={() => { void setNativeAudioRoute(!speakerOn); }}
                style={callControlStyle(speakerOn)}
              >
                {speakerOn ? 'SPEAKER' : 'RECEIVER'}
              </button>
              <button
                type="button"
                aria-label={ptt.muted ? 'Unmute' : 'Mute'}
                aria-pressed={ptt.muted}
                onClick={ptt.toggleMute}
                style={callControlStyle(ptt.muted)}
              >
                {ptt.muted ? 'MUTED' : 'MUTE'}
              </button>
            </div>
          ) : null}
        </div>
      ) : (
        // No reserved slot (Chat) — the free-floating compact control. CornerOrb
        // unchanged: persisted position, viewport-clamped, and its own
        // tap-versus-drag discipline so repositioning can never hang up a call.
        // Rendered ONLY for a page that has reported it is slotless, never during
        // the unknown window of a navigation.
        pageIsSlotless ? <CornerOrb /> : null
      )}
    </AppShellLiveContext.Provider>
  );
}
